import {
  AppIcon,
  BackrsUser,
  HorizontalMenuTabs,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import {
  Box,
  Typography,
  SxProps,
  Theme,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { FieldValues } from 'react-hook-form';

import { SectionHeader } from '../atoms/SectionHeader';

const SETTINGS_STRINGS = appStrings.modules.settings;
const BUTTON_STRINGS = appStrings.common.button;

const TABS = {
  Settings: { tab: 'Settings', disabled: false },
  Networks: { tab: 'Networks', disabled: true },
  Notifications: { tab: 'Notifications', disabled: true },
  Connections: { tab: 'Connections', disabled: true },
} as const;

type SettingsHeaderProps = Readonly<{
  activeTab: number;
  setActiveTab: (tab: number) => void;
  reset: (data: FieldValues) => void;
  onSubmit: () => void;
  user: BackrsUser;
  isLoading?: boolean;
  formSaveError: string | null;
  setFormSaveError: (error: string | null) => void;
}>;

export const SettingsHeader = ({
  activeTab,
  setActiveTab,
  reset,
  onSubmit,
  user,
  isLoading = true,
  formSaveError,
  setFormSaveError,
}: SettingsHeaderProps) => {
  const tabs = Object.values(TABS).map((tab) => tab.tab);
  const disabledTabs = Object.values(TABS)
    .filter((tab) => tab.disabled)
    .map((tab) => tab.tab);

  return (
    <Box sx={containerSx}>
      <Typography variant="display-xs-semibold">{SETTINGS_STRINGS.header.title}</Typography>
      <Box sx={horizontalMenuSxLargerScreens}>
        <HorizontalMenuTabs
          tabs={tabs}
          value={activeTab}
          setValue={setActiveTab}
          variant="box"
          disabledTabs={disabledTabs}
        />
      </Box>

      {/* SELECT FOR SMALL SCREENS */}
      <Box sx={phoneSizeScreenMenuDropdown}>
        <Select
          value={tabs[activeTab]}
          defaultValue={tabs[0]}
          sx={selectSx}
          IconComponent={({ className }) => {
            const isSelectOpen = className.includes('MuiSelect-iconOpen');

            return (
              <Box sx={smallScreenHeaderSx(isSelectOpen)}>
                <AppIcon iconName="ChevronDown" diameter="16px" color="Gray/600" />
              </Box>
            );
          }}
        >
          {Object.entries(TABS).map(([tab, value]) => {
            return (
              <MenuItem
                key={tab}
                value={tab}
                disabled={value.disabled}
                onClick={() => setActiveTab(Object.values(TABS).indexOf(value))}
              >
                <Typography variant="text-md-regular">{value.tab}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      <SectionHeader
        title={SETTINGS_STRINGS.header.title}
        description={SETTINGS_STRINGS.header.description}
        actions={
          <Box sx={headerActionsContainerSx}>
            <Box sx={actionsContainerSx}>
              <Button
                hierarchy="Secondary gray"
                size="md"
                onClick={() => {
                  reset({
                    firstName: user.first_name,
                    lastName: user.last_name,
                    email: user.email,
                    phone: user.phone,
                    zipCode: user.zip_code,
                    birthday: user.birthday,
                    newPassword: '',
                    confirmPassword: '',
                  });

                  setFormSaveError(null);
                }}
                sx={buttonSx}
              >
                {BUTTON_STRINGS.cancel}
              </Button>
              <Button hierarchy="Primary" size="md" onClick={onSubmit} sx={buttonSx}>
                {/* when we are saving, instead of hiding the text we will set opacity to 0 so that the button does not change size */}
                <Typography variant="text-md-semibold" sx={isLoading ? { opacity: 0 } : {}}>
                  {BUTTON_STRINGS.save}
                </Typography>

                {isLoading && <CircularProgress sx={loadingContainerSx} size={20} />}
              </Button>
            </Box>
            {formSaveError && (
              <Typography
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Error/500']}
              >
                {formSaveError}
              </Typography>
            )}
          </Box>
        }
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const headerActionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: { xs: 'fixed', sm: 'static' },
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: backrs.colors['Colors/Base/White'],
  padding: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
  borderTop: { xs: `1px solid ${backrs.colors['Gray/200']}`, sm: 'none' },
  zIndex: 10,

  paddingLeft: {
    xs: `calc(${SIDEBAR_COLLAPSED_WIDTH}px + ${backrs.spacing['spacing-md']})`,
    sm: backrs.spacing['spacing-none'],
  },
});

const actionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
});

const smallScreenHeaderSx =
  (isSelectOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    transition: 'all .3s ease-in-out',
    mb: isSelectOpen ? backrs.spacing['spacing-xs'] : `-${backrs.spacing['spacing-xs']}`,
    transform: isSelectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  });

const phoneSizeScreenMenuDropdown: SxProps<Theme> = ({ breakpoints }) => ({
  [breakpoints.up('sm')]: {
    display: 'none',
  },
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-md'],
});

const horizontalMenuSxLargerScreens: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  color: backrs.colors['Colors/Base/White'],

  position: 'absolute',
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  height: backrs.spacing['spacing-5xl'],
});
